import * as React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import {
  List,
  ListItemText,
  ListItemIcon,
  useMediaQuery,
  Tooltip,
  Badge,
  Box,
} from '@mui/material';
import Collapse from '@mui/material/Collapse';
import { Link, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { useEffect } from 'react';
import SidebarListSubItem from './SidebarListSubItem';

import { CustomListItem, CustomListItemButton } from './index';
import { useState } from 'react';

const ListItemToggleIcon = styled(Box)(({ theme }) => ({
  width: '20px', // Exact Figma spec: 20px toggle icon
  height: '20px',
  transition: 'transform 0.2s ease-in-out, background-color 0.2s ease-in-out',
  marginTop: '-6px',
  maskRepeat: 'no-repeat',
  maskPosition: 'center',
  maskSize: 'contain',
  WebkitMaskRepeat: 'no-repeat',
  WebkitMaskPosition: 'center',
  WebkitMaskSize: 'contain',
  backgroundColor: 'currentColor',
  '&.rotated': {
    transform: 'rotate(180deg)',
  },
  '&.visible': {
    display: 'none',
  },
  // Responsive sizing between md and xl
  [theme.breakpoints.only('md')]: {
    width: '18px',
    height: '18px',
    marginTop: '-4px',
  },
  [theme.breakpoints.only('lg')]: {
    width: '20px',
    height: '20px',
    marginTop: '-5px',
  },
}));

// Updated ToggleIconButton with fixed styling
const ToggleIconButton = styled(Box)(({ theme }) => ({
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(1, 3),
  zIndex: 2,
  position: 'absolute',
  right: 12,
  height: '100%',

  // Important - this makes the toggle button transparent to hover events
  pointerEvents: 'all',

  // Disable touch highlight effects on all platforms
  WebkitTapHighlightColor: 'transparent',

  // Prevent all background color changes
  background: 'transparent !important',
  backgroundColor: 'transparent !important',

  // Prevent Material UI's ripple effect
  '& .MuiTouchRipple-root': {
    display: 'none',
  },

  // Override all interactive states
  '&:hover, &:active, &:focus, &:focus-visible, &:focus-within, &.Mui-focusVisible':
    {
      background: 'transparent !important',
      backgroundColor: 'transparent !important',
      outline: 'none',
    },

  // Specific fixes for mobile devices
  [theme.breakpoints.down('md')]: {
    '&:hover, &:active, &:focus, &::before, &::after': {
      background: 'transparent !important',
      backgroundColor: 'transparent !important',
    },
  },
}));

const CustomListItemText = styled(ListItemText)(({ theme }) => ({
  // color: theme.palette.mode === 'dark' ? '#fff' : '#000',
  fontSize: '14px !important',
}));

// Utility function to determine if the path belongs to a specific section
const isPathInSection = (path, sectionName) => {
  if (!path) return false;
  return path.includes(sectionName);
};

// Utility function to determine if the path belongs to any subitem
const isPathInSubtree = (path, subItems) => {
  if (!subItems || !path) return false;

  return subItems.some(
    (subItem) =>
      path === subItem.link ||
      path.startsWith(subItem.link + '/') ||
      (subItem.subItems && isPathInSubtree(path, subItem.subItems))
  );
};

const SidebarListItem = ({ item, t, sidebarOpen }) => {
  const hostname = window.location.hostname;
  const subdomain = hostname.split('.')[0];
  const isPanel = subdomain.includes('panel');
  const isPlayground = !isPanel;

  const location = useLocation();
  const path = location.pathname;

  const isActive =
    (item.text === 'Playground' && !sidebarOpen && isPlayground) ||
    (item.link &&
      (item.link === '/' ? path === '/' : path.startsWith(item.link)));

  // Track manually toggled state (user-initiated) and the desired state from that toggle
  const [manuallyToggled, setManuallyToggled] = useState(false);
  const [manualToggleState, setManualToggleState] = useState(false);

  // Determine if this item should be open based on the current route
  const shouldBeOpen = React.useMemo(() => {
    // Special case for Playground in Playground mode
    if (item.text === 'Playground' && isPlayground) {
      return true;
    }

    // Otherwise, check if the current path is in this item's subtree
    return isPathInSubtree(path, item.subItems);
  }, [item.text, item.subItems, path, isPlayground]);

  // Derive the actual open state, preferring manual toggle over route-based state
  const [open, setOpen] = useState(shouldBeOpen);

  // Update open state whenever the route changes
  useEffect(() => {
    if (manuallyToggled) {
      // If manually toggled, use the manual toggle state
      setOpen(manualToggleState);
    } else {
      // Otherwise use route-based state
      setOpen(shouldBeOpen);
    }
  }, [shouldBeOpen, manuallyToggled, manualToggleState]);

  // Determine which subitems should be open based on the current route
  const derivedSubItemState = React.useMemo(() => {
    if (!sidebarOpen) return {};

    return {
      data: isPathInSection(path, 'data'),
      resume: isPathInSection(path, 'resume'),
      youtube: isPathInSection(path, 'youtube'),
    };
  }, [path, sidebarOpen]);

  // Store the derived state as our actual state
  const [openSubItems, setOpenSubItems] = useState(derivedSubItemState);

  // Update subitem states whenever route changes
  useEffect(() => {
    setOpenSubItems(derivedSubItemState);
  }, [derivedSubItemState]);

  const theme = useTheme();
  const mdDown = useMediaQuery(theme.breakpoints.down('md'));

  const updateOpenSubItems = (itemName, openState) => {
    setOpenSubItems((prevState) => ({
      ...prevState,
      [itemName]: openState,
    }));
  };

  const hasChildren = item.subItems && item.subItems.length > 0;

  // Handle toggle button click
  const handleToggleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!hasChildren) return;

    // Toggle the open state
    const newOpenState = !open;

    // Set manual toggle state to track what the user wants
    setManuallyToggled(true);
    setManualToggleState(newOpenState);
    setOpen(newOpenState);

    // Never auto-reset the manual toggle state when closing
    // This allows the user to keep a dropdown closed even if route matches

    // Reset subitems if closing
    if (!newOpenState) {
      setOpenSubItems({
        resume: false,
        youtube: false,
        data: false,
      });
    }
  };

  // Handle menu item click
  const handleItemClick = () => {
    // Special case for Playground in Playground mode
    if (item.text === 'Playground' && isPlayground && sidebarOpen) {
      const newOpenState = !open;

      // Set manual toggle state to track what the user wants
      setManuallyToggled(true);
      setManualToggleState(newOpenState);
      setOpen(newOpenState);

      // If closing, reset subitems
      if (open) {
        setOpenSubItems({
          resume: false,
          youtube: false,
          data: false,
        });
      }
    }
  };

  // Close everything when sidebar closes
  useEffect(() => {
    if (!sidebarOpen) {
      setOpen(false);
      setOpenSubItems({
        resume: false,
        youtube: false,
        data: false,
      });
      // Don't reset manual toggle state when sidebar closes
      // This preserves user preferences across sidebar open/close
    } else {
      // When sidebar opens, respect manual toggle state if set
      if (manuallyToggled) {
        setOpen(manualToggleState);
      } else {
        // Otherwise use route-based state
        setOpen(shouldBeOpen);
        setOpenSubItems(derivedSubItemState);
      }
    }
  }, [
    sidebarOpen,
    shouldBeOpen,
    derivedSubItemState,
    manuallyToggled,
    manualToggleState,
  ]);

  // Determine if this item should act as a link or just a toggle container
  const shouldUseLink = true; // Always allow links

  // Custom component to use as the base for the list item
  const ItemComponent = item.link ? (item.external ? 'a' : Link) : 'div';

  return (
    <>
      <CustomListItem
        key={item.text}
        disablePadding
        sx={{
          display: 'block',
          // Responsive margin-bottom between md and xl
          mb: 0,
          [theme.breakpoints.only('md')]: {
            mb: 0.5,
          },
          [theme.breakpoints.only('lg')]: {
            mb: 0.25,
          },
        }}
      >
        <Tooltip
          placement="right"
          arrow={true}
          title={sidebarOpen ? '' : t(`sidebar.${item.text}`)}
        >
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              // This ensures the entire row (including toggle) gets hover styles
              '&:hover .MuiListItemButton-root': {
                backgroundColor: (theme) =>
                  theme.palette.mode === 'dark' ? '#464646' : '#E1F2FD',
                '& .MuiListItemText-root, & .MuiListItemIcon-root, & svg, .toggle-icon':
                  {
                    color: (theme) => theme.palette.primary.main,
                  },
              },
            }}
          >
            <CustomListItemButton
              onClick={handleItemClick}
              LinkComponent={ItemComponent}
              to={
                item.text === 'Playground' && isPlayground
                  ? '/' // Link to root route when in Playground
                  : item.link && !item.external
                    ? item.link
                    : undefined
              }
              href={item.link && item.external ? item.link : undefined}
              target={
                shouldUseLink && item.external && item.link
                  ? item.target
                  : undefined
              }
              isActive={isActive.toString()}
              sidebarOpen={sidebarOpen}
              sx={{
                width: '100%',
                pr: hasChildren && sidebarOpen ? 8 : undefined, // Make space for the toggle icon
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  width: '24px', // Exact Figma spec: 24px icon size
                  height: '24px',
                  mr: sidebarOpen ? '12px' : 0, // Exact Figma spec: 12px gap
                  justifyContent: 'center',
                  position: 'relative',
                  color:
                    theme.palette.mode === 'dark'
                      ? theme.palette.primary.primary_100
                      : theme.palette.primary.main,
                  // Responsive sizing between md and xl
                  [theme.breakpoints.only('md')]: {
                    width: '20px',
                    height: '20px',
                    mr: sidebarOpen ? '8px' : 0,
                    marginTop: '-0px', // Move up as icon shrinks
                    // Make child SVG icons responsive
                    '& .sidebar-icon, & svg': {
                      width: '20px !important',
                      height: '20px !important',
                    },
                  },
                  [theme.breakpoints.only('lg')]: {
                    width: '22px',
                    height: '22px',
                    mr: sidebarOpen ? '10px' : 0,
                    marginTop: '-0px', // Move up slightly
                    // Make child SVG icons responsive
                    '& .sidebar-icon, & svg': {
                      width: '22px !important',
                      height: '22px !important',
                    },
                  },
                }}
              >
                {item.badge ? (
                  <Badge
                    sx={{
                      '.MuiBadge-badge': {
                        background:
                          theme.palette.mode === 'dark'
                            ? `${theme.palette.secondary.secondary_60} !important`
                            : `${theme.palette.secondary.secondary_20} !important`,
                      },
                    }}
                    color="secondary"
                    overlap="circular"
                    badgeContent=" "
                    variant="dot"
                  >
                    <item.icon
                      size={24}
                      style={{
                        width: '24px',
                        height: '24px',
                      }}
                      className="sidebar-icon"
                    />
                  </Badge>
                ) : (
                  <item.icon
                    size={24}
                    style={{
                      width: '24px',
                      height: '24px',
                    }}
                    className="sidebar-icon"
                  />
                )}
              </ListItemIcon>
              <CustomListItemText
                primary={t(`sidebar.${item.text}`)}
                primaryTypographyProps={{
                  sx: {
                    fontSize: {
                      xs: isActive ? '13px !important' : '14px !important',
                      md: isActive ? '12px !important' : '13px !important',
                      lg: isActive ? '13px !important' : '14px !important',
                      xl: isActive ? '15px !important' : '14px !important',
                    },
                    fontWeight: isActive ? 700 : 400,
                  },
                }}
                sx={{
                  display: sidebarOpen ? 'block' : 'none',
                  color: 'secondary.neutral_60',
                }}
              />
            </CustomListItemButton>

            {/* Separate toggle icon outside of the link area */}
            {hasChildren && sidebarOpen && (
              <ToggleIconButton
                onClick={handleToggleClick}
                component="div"
                disableTouchRipple
                sx={{
                  backgroundColor: 'transparent !important',
                  color: isActive
                    ? theme.palette.mode === 'dark'
                      ? '#8CCFF9'
                      : '#0486D7'
                    : theme.palette.mode === 'dark'
                      ? '#C6C6C6'
                      : '#787878',
                  '&:hover, &:active, &:focus': {
                    backgroundColor: 'transparent !important',
                    color: (theme) => theme.palette.primary.main,
                  },
                }}
              >
                <ListItemToggleIcon
                  className={open ? 'rotated' : ''}
                  sx={{
                    maskImage: 'url(/static/img/icon/arrow-down-light.svg)',
                    WebkitMaskImage:
                      'url(/static/img/icon/arrow-down-light.svg)',
                  }}
                />
              </ToggleIconButton>
            )}
          </Box>

          {/* Only render the Collapse component if sidebar is open */}
          {sidebarOpen && (
            <Collapse
              in={open}
              timeout={300}
              unmountOnExit
              sx={{
                willChange: 'height',
                overflow: 'hidden',
                transition: 'height 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                '& .MuiCollapse-wrapper': {
                  willChange: 'height',
                },
                '& .MuiCollapse-wrapperInner': {
                  willChange: 'height',
                },
              }}
            >
              {item?.subItems?.map((subItem) => (
                <List component="div" disablePadding key={subItem.text}>
                  <SidebarListSubItem
                    item={subItem}
                    t={t}
                    sidebarOpen={sidebarOpen}
                    depth={0.6}
                    updateOpenSubItems={updateOpenSubItems}
                    openSubItems={openSubItems}
                    open={openSubItems[subItem.text]}
                    isPanel={isPanel}
                    isPlayground={isPlayground}
                  />
                </List>
              ))}
            </Collapse>
          )}
        </Tooltip>
      </CustomListItem>
    </>
  );
};

export default SidebarListItem;
